import React, { useState, useEffect, useRef } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { capabilities, terminalLines } from '../data/mock';
import { Cpu, Layers, Palette, Search, Star, TrendingUp, Briefcase, ChevronDown, Settings, Users, BarChart3, Shield } from 'lucide-react';

const iconMap = {
  cpu: Cpu,
  layers: Layers,
  palette: Palette,
  search: Search,
  star: Star,
  trendingUp: TrendingUp,
  briefcase: Briefcase,
  users: Users,
  barChart: BarChart3,
  shield: Shield,
};

function CapabilityCard({ capability, index }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = iconMap[capability.icon] || Cpu;

  return (
    <div
      className={`border-t border-white/5 py-6 group cursor-pointer transition-all duration-300 hover:bg-white/[0.02] px-2 -mx-2 rounded ${
        index === 0 ? 'border-t-0' : ''
      }`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 md:gap-6 flex-1">
          <div className="w-10 h-10 rounded-lg border border-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-1 group-hover:border-emerald-500/30 transition-colors">
            <Icon size={18} className="text-emerald-500/60" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-mono text-base md:text-lg font-bold text-white uppercase tracking-[0.05em] group-hover:text-emerald-400 transition-colors">
                {capability.title}
              </h3>
              <span className="font-mono text-[10px] tracking-[0.2em] text-emerald-500/30">
                // {capability.number}
              </span>
            </div>
            <p className="font-mono text-xs leading-relaxed text-white/35">
              {capability.description}
            </p>

            {/* Expanded Content */}
            <div
              className={`overflow-hidden transition-all duration-500 ${
                expanded ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="flex flex-wrap gap-2">
                {capability.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[10px] tracking-[0.1em] px-3 py-1.5 rounded-full border border-emerald-500/15 text-emerald-400/60"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <button className="font-mono text-[10px] tracking-[0.15em] text-white/25 hover:text-emerald-400 transition-colors flex items-center gap-1 flex-shrink-0 mt-2">
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
          />
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
    </div>
  );
}

function TerminalBlock() {
  const [displayedLines, setDisplayedLines] = useState([]);
  const [ref, isVisible] = useScrollReveal({ threshold: 0.3 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let lineIndex = 0;
      const interval = setInterval(() => {
        if (lineIndex < terminalLines.length) {
          const line = terminalLines[lineIndex];
          lineIndex++;
          setDisplayedLines(prev => [...prev, line]);
        } else {
          clearInterval(interval);
        }
      }, 600);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <div ref={ref} className="mt-16 md:mt-24">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full bg-emerald-500" />
        <span className="font-mono text-[10px] tracking-[0.2em] text-emerald-500/50 uppercase">
          SYSTEM: OPTIMIZED
        </span>
      </div>
      <div className="bg-[#0a0a0a] rounded-xl border border-emerald-500/10 p-6 md:p-8 font-mono text-sm">
        {displayedLines.map((line, i) => (
          <div
            key={i}
            className={`${
              line.type === 'command' ? 'text-emerald-400/80' : 'text-white/35'
            } ${
              line.type === 'cursor' ? 'terminal-cursor' : ''
            } mb-1 animate-fadeIn`}
          >
            {line.text}
          </div>
        ))}
        {displayedLines.length === 0 && (
          <div className="text-emerald-500/30 terminal-cursor">_</div>
        )}
      </div>
    </div>
  );
}

export default function CapabilitiesSection() {
  const [headerRef, headerVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="capabilities" className="bg-[#080808] py-20 md:py-32 relative overflow-hidden">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 capabilities-grid-bg opacity-40" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`mb-16 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-2 mb-4">
            <Settings size={16} className="text-emerald-500/50" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase">
              System Capabilities
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white tracking-[-0.02em] uppercase">
              Capabilities
            </h2>
            <p className="font-mono text-xs tracking-[0.15em] text-white/30 text-right uppercase">
              Digital Solutions &<br />
              AI Integration
            </p>
          </div>
        </div>

        {/* Capabilities List */}
        <div className="border-t border-white/5">
          {capabilities.map((cap, i) => (
            <CapabilityCard key={cap.id} capability={cap} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
